import { useState } from "react";
import { Sparkles, Loader2 } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";

/** One-click cleanup of role grants that the role's audience defaults already
 *  cover. Preview first, then apply. */
export default function TidyGrants({ canManage, onTidied }) {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [applying, setApplying] = useState(false);
  const [plan, setPlan] = useState(null);

  if (!canManage) return null;

  const preview = async () => {
    setOpen(true);
    setLoading(true);
    setPlan(null);
    try {
      const { data } = await api.get("/roles/redundant-grants");
      setPlan(data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
      setOpen(false);
    } finally { setLoading(false); }
  };

  const apply = async () => {
    setApplying(true);
    try {
      const { data } = await api.post("/roles/tidy-grants", { role_ids: [] });
      toast.success(`Removed ${data.removed} redundant grant${data.removed === 1 ? "" : "s"} across ${data.roles_tidied} role${data.roles_tidied === 1 ? "" : "s"}`);
      setOpen(false);
      onTidied?.();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally { setApplying(false); }
  };

  return (
    <>
      <Button variant="outline" size="sm" className="h-8 text-xs" onClick={preview} data-testid="tidy-grants-button">
        <Sparkles className="mr-1.5 h-3.5 w-3.5" /> Tidy redundant grants
      </Button>

      <Dialog open={open} onOpenChange={(o) => !o && setOpen(false)}>
        <DialogContent className="max-w-2xl" data-testid="tidy-grants-dialog">
          <DialogHeader>
            <DialogTitle>Tidy redundant grants</DialogTitle>
            <DialogDescription>
              These role grants are already given by the audience defaults the role inherits from, so removing them
              changes nobody&apos;s access — the matrix just gets easier to read. For partner roles a grant is only
              listed when every matching partner-type audience already carries it.
            </DialogDescription>
          </DialogHeader>

          {loading && (
            <div className="flex items-center gap-2 py-8 text-sm text-slate-500" data-testid="tidy-grants-loading">
              <Loader2 className="h-4 w-4 animate-spin" /> Checking every role…
            </div>
          )}

          {!loading && plan && plan.total === 0 && (
            <p className="py-8 text-center text-sm text-slate-500" data-testid="tidy-grants-empty">
              Nothing to tidy — no role duplicates its audience defaults.
            </p>
          )}

          {!loading && plan?.total > 0 && (
            <div className="max-h-[50vh] space-y-3 overflow-auto" data-testid="tidy-grants-list">
              {plan.roles.map((r) => (
                <div key={r.role_id} className="rounded-lg border border-slate-200 p-3" data-testid={`tidy-role-${r.role_id}`}>
                  <div className="flex flex-wrap items-baseline justify-between gap-2">
                    <span className="text-sm font-medium text-slate-800">{r.name}</span>
                    <span className="text-xs text-slate-500" data-testid={`tidy-count-${r.role_id}`}>
                      {r.redundant.length} of {r.grants} grants redundant
                    </span>
                  </div>
                  <p className="mt-0.5 text-[11px] text-slate-400">
                    Covered by: {r.audiences.map((a) => a.label).join(", ")}
                  </p>
                  <div className="mt-2 flex flex-wrap gap-1">
                    {r.redundant.map((p) => (
                      <span key={p} className="rounded bg-slate-100 px-1.5 py-0.5 font-mono text-[10px] text-slate-600">{p}</span>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)} data-testid="tidy-grants-cancel">Close</Button>
            {plan?.total > 0 && (
              <Button onClick={apply} disabled={applying} data-testid="tidy-grants-apply">
                {applying && <Loader2 className="mr-1.5 h-4 w-4 animate-spin" />}
                Remove {plan.total} grant{plan.total === 1 ? "" : "s"}
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
